import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Menu } from '@mantine/core';
import { Copy, Dots, Pencil, Plus, Stack, Trash } from 'tabler-icons-react';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import TableView from '../../common/TableView';
import { triggerNotification } from '../../../helpers/notification';
import HeaderView from '../../common/HeaderView';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppMenu from '../../common/AppMenu';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import ConfirmModal from '../../common/ConfirmModal';

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Products',
    value: 'product_count',
    sortable: true
  }
];

const VIEW_ACTIONS = [
  {
    label: 'Edit',
    value: VIEW_ACTIONS_ENUM.EDIT,
    section: 1,
    icon: <Pencil size={18} />
  },
  {
    label: 'Duplicate',
    value: VIEW_ACTIONS_ENUM.DUPLICATE,
    section: 1,
    icon: <Copy size={18} />
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.DELETE,
    icon: <Trash color="red" size={18} />,
    section: 2,
    color: 'red'
  }
];

const VendorDesignsTable = () => {
  const fetchedPk = useRef(null);
  const navigate = useNavigate();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchPaginatedVendorDesigns,
    deleteVendorDesigns
  } = useContext(VendorContext);
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      showStoreBundles: false,
      fkEcomCategory: null,
      fkEcomProductStatus: null
    },
    (newFilter) =>
      fetchPaginatedVendorDesigns(
        authState.pkEcomVendor,
        newFilter,
        null,
        triggerNotification
      ),
    state.paginatedEcomVendorDesigns
  );

  const loading = !fetchedPk.current || dataLoading;
  const noEntitiesCreated = !loading && totalNoFilterCount === 0;

  const hasFiltered = !!filter.search;

  useEffect(() => {
    if (authState.pkEcomVendor) {
      onRefresh();
      fetchedPk.current = authState.pkEcomVendor;
    }
  }, [authState.pkEcomVendor]);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: 'Designs',
          to: '/vendor/designs'
        }
      ]}
      rightSection={
        !loading && (
          <Button
            color="dark"
            leftSection={<Plus size={18} />}
            onClick={() => navigate('/vendor/designs/new')}
            radius="md"
            size="compact-md"
            style={{ fontSize: 14 }}
          >
            Create design
          </Button>
        )
      }
    >
      <TableView
        columns={TABLE_COLUMNS}
        disabled={noEntitiesCreated && !hasFiltered}
        disabledContent={{
          title: 'Create your first design',
          description:
            'Designs are visual representations of your products. Add your first design to get started. You can always add more designs later.',
          icon: <Stack color="#000" size={125} />,
          primaryButton: {
            label: 'Create design',
            icon: <Plus size={18} />,
            link: '/vendor/designs/new'
          }
        }}
        emptyMessage="No designs found."
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by name',
            value: filter.search || '',
            onChange: (value) => onFilterChange({ ...filter, search: value })
          }
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={loading}
        noSort
        onAction={(action, item) => {
          switch (action) {
            case VIEW_ACTIONS_ENUM.EDIT:
              navigate(`/vendor/designs/${item.key}`);
              break;
            case VIEW_ACTIONS_ENUM.DUPLICATE:
              navigate(`/vendor/designs/${item.key}/duplicate?from=designs`);
              break;
            case VIEW_ACTIONS_ENUM.DELETE:
              onOpenModal(action, [
                data.find((s) => s.pkEcomVendorDesign === item.key)
              ]);
              break;
            default:
              onOpenModal(
                action,
                data.find((s) => s.pkEcomVendorDesign === item.key)
              );
              break;
          }
        }}
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={data.map((a) => ({
          key: a.pkEcomVendorDesign,
          actions: VIEW_ACTIONS,
          columns: [
            {
              key: 1,
              uuid: a.uuid,
              label: a.name,
              to: `/vendor/designs/${a.pkEcomVendorDesign}`,
              component: Link,
              weight: 500,
              subLabel: a.description,
              src: a.previewImage ?? null,
              showImage: true
            },
            {
              key: 2,
              label: a.ecomVendorDesignProductCount,
              sortValueType: 'number'
            }
          ]
        }))}
        selectActionSection={
          <AppMenu
            control={
              <ActionIcon color="#ced4da" size="lg" variant="outline">
                <Dots color="#000" />
              </ActionIcon>
            }
            styles={{ dropdown: { padding: 0 } }}
          >
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Trash size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(VIEW_ACTIONS_ENUM.DELETE, [
                    ...data.filter((s) =>
                      selectedRows.includes(s.pkEcomVendorDesign)
                    )
                  ]);
                }}
                style={{ color: 'red' }}
              >
                Delete designs
              </Menu.Item>
            </AppStack>
          </AppMenu>
        }
        selectedRows={selectedRows}
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Designs"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete designs"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText style={{ fontSize: 16 }}>
              Are you sure you want to delete{' '}
              <b>
                {modalState.item?.length === 1
                  ? modalState.item[0]?.name
                  : `${modalState.item?.length} designs`}
              </b>
              ?
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action cannot be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteVendorDesigns(
            authState.pkEcomVendor,
            modalState.item.map((i) => i.pkEcomVendorDesign),
            () => {
              triggerNotification(
                modalState.item.length === 1
                  ? 'Design deleted.'
                  : 'Designs deleted.',
                'success'
              );
              onCloseModal();
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${
          modalState.item?.length === 1
            ? modalState.item[0]?.name
            : `${modalState.item?.length} designs`
        }?
        `}
      />
    </HeaderView>
  );
};

VendorDesignsTable.propTypes = {};

export default VendorDesignsTable;
